import api from './api';
import type {
  UserListItem,
  UserCreateRequest,
  UserUpdateRequest,
  UserFilters,
  PaginatedResponse,
} from '@/types';

/** Board #180: the password an admin types is now OPTIONAL. Leaving it out has
 *  the server generate one and hand it back in the 201 (see `CreatedUser`).
 *  Declared here rather than widened in `@/types` because `UserCreateRequest`
 *  is shared with paths that still require a password. */
export type AdminUserCreatePayload = Omit<UserCreateRequest, 'password'> & {
  password?: string;
  country?: string;
};

/** The one-time credentials block on a 201. Present only when the SERVER
 *  generated the password — when the admin typed one there is nothing to
 *  reveal, and the field is absent. It is never readable again: the value is
 *  hashed on the way in, so no later GET can return it. */
export interface IssuedCredentials {
  email: string | null;
  phone: string | null;
  password: string;
  must_change_password: boolean;
  message: string;
}

export type CreatedUser = UserListItem & { credentials?: IssuedCredentials };

/**
 * Board #180: Active / Suspend / Block are three DISTINCT server-side states
 * (`User.status`), not a boolean with two labels. Suspended and blocked are
 * both barred from logging in and differ in intent; the login form says which.
 *
 * `is_active` still exists on the row and is still the authentication gate —
 * it is derived from `status` server-side, and the two can never disagree.
 * Read `status` for display; it is the only one that can tell suspended from
 * blocked.
 */
export type UserStatus = 'active' | 'suspended' | 'blocked';

export const USER_STATUS_VALUES: UserStatus[] = ['active', 'suspended', 'blocked'];

/**
 * A user row as this API actually serves it.
 *
 * `status`/`status_display` are optional because `UserListItem` (src/types) is
 * shared with screens that predate the column; treat a row without one as
 * unknown rather than inventing "Active" for it — see `userStatusOf`.
 */
export type UserWithStatus = UserListItem & {
  status?: UserStatus;
  status_display?: string;
  /** Only on the set-status / activate responses: an insurer login with no insurer attached. */
  insurer_association_missing?: boolean;
};

export const INSURER_ASSOCIATION_MISSING_HINT =
  'Their login is restored. They cannot reach the insurer portal until they are added again as an employee of the insurer.';

/**
 * The status to render for a row, or `null` when the server did not say.
 *
 * Deliberately NOT falling back to `is_active ? 'active' : 'suspended'`: that
 * guess renders "Suspended" over a blocked account, which is precisely the
 * distinction this column was added to make. A caller that gets `null` should
 * show what it does know (`is_active`), not a state nobody asserted.
 */
export function userStatusOf(user: UserWithStatus): UserStatus | null {
  return user.status && USER_STATUS_VALUES.includes(user.status) ? user.status : null;
}

/** Body of `POST /users/{id}/reset_password/` — the password is shown once. */
export interface UserPasswordResetResponse {
  user_id: string;
  credentials: {
    email: string | null;
    phone: string | null;
    password: string;
    must_change_password: boolean;
    message: string;
  };
}

/** `UserFilters` plus the three-state status the list now filters on. */
export type UserListFilters = UserFilters & { status?: UserStatus };

export const userService = {
  async getUsers(
    page: number = 1,
    filters?: UserListFilters
  ): Promise<PaginatedResponse<UserWithStatus>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    // Three-state filter (board #180). Sent as `?status=` — `?is_active=false`
    // cannot separate suspended from blocked, so the Status dropdown asks the
    // server the question it can actually answer.
    if (filters?.status) params.append('status', filters.status);
    if (filters?.tenant_type) params.append('tenant_type', filters.tenant_type);

    const response = await api.get<PaginatedResponse<UserListItem>>(`/users/?${params.toString()}`);
    return response.data;
  },

  async getUser(id: string): Promise<UserWithStatus> {
    const response = await api.get<UserWithStatus>(`/users/${id}/`);
    return response.data;
  },

  async createUser(data: AdminUserCreatePayload): Promise<CreatedUser> {
    const response = await api.post<CreatedUser>('/users/', data);
    return response.data;
  },

  async updateUser(id: string, data: UserUpdateRequest): Promise<UserListItem> {
    const response = await api.patch<UserListItem>(`/users/${id}/`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}/`);
  },

  async activateUser(id: string): Promise<UserWithStatus> {
    const response = await api.post<UserWithStatus>(`/users/${id}/activate/`);
    return response.data;
  },

  async deactivateUser(id: string): Promise<UserWithStatus> {
    const response = await api.post<UserWithStatus>(`/users/${id}/deactivate/`);
    return response.data;
  },

  /**
   * Issue a new temporary password for a user.
   *
   * The return value is the WHOLE point and used to be discarded: this was
   * typed `Promise<void>` and awaited the call without reading it, so even once
   * the endpoint existed the one thing it produces -- a password no one can
   * recover afterwards -- went in the bin, and the UI told the admin an email
   * had been sent. No delivery channel is configured on this platform, so the
   * password reaches the user only if a human passes it on. Show it.
   */
  async resetPassword(id: string): Promise<UserPasswordResetResponse> {
    const response = await api.post<UserPasswordResetResponse>(`/users/${id}/reset_password/`);
    return response.data;
  },

  /**
   * Set a user to Active, Suspended or Blocked.
   *
   * The server decides whether this caller may govern this target's role
   * (broker admins run POSPs and employees; country admins run broker admins)
   * and answers 403/404 when they may not — this call does not pre-judge that,
   * it surfaces the refusal.
   */
  async setStatus(id: string, status: UserStatus): Promise<UserWithStatus> {
    const response = await api.post<UserWithStatus>(`/users/${id}/set-status/`, { status });
    return response.data;
  },
};

export async function getCountryAdmins(params?: { page?: number; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.search) searchParams.set('search', params.search);
  const response = await api.get(`/users/country-admins/?${searchParams}`);
  return response.data;
}

export async function toggleCountryAdminActive(userId: string) {
  const response = await api.post(`/users/${userId}/toggle-active/`);
  return response.data;
}

export async function getCountryAdminDrilldown(userId: string) {
  const response = await api.get(`/users/${userId}/drilldown/`);
  return response.data;
}

export async function getPlatformStats() {
  const response = await api.get('/users/platform-stats/');
  return response.data;
}
